import { useEffect, useMemo, useState } from "react";

// @mui
import Grid from "@mui/material/Grid";

// ZionLadder UI
import MKBox from "components/MKBox";

import Question from "./Question";

import PropTypes from "prop-types";

function FAQ({ items, defaultExpanded }) {
  // Normalize incoming items to a stable array shape
  const normalizedItems = useMemo(() => {
    if (!Array.isArray(items)) return [];
    return items
      .filter(Boolean)
      .map((it) => {
        const q =
          typeof it.q === "string" ? it.q : typeof it.question === "string" ? it.question : "";

        const a = typeof it.a === "string" ? it.a : typeof it.answer === "string" ? it.answer : "";

        // Prefer explicit key, else pk from backend, else empty (fallback to idx in render)
        const key =
          typeof it.key === "string" && it.key
            ? it.key
            : typeof it.pk === "number" || typeof it.pk === "string"
            ? `faq-${it.pk}`
            : "";

        return { q, a, key };
      })
      .filter((it) => it.q || it.a);
  }, [items]);

  // collapsed by default (or expanded by default if defaultExpanded=true)
  const [openMap, setOpenMap] = useState(() => ({}));

  // When the incoming items set changes, reset expansion state
  useEffect(() => {
    if (!defaultExpanded) {
      setOpenMap({});
      return;
    }
    const next = {};
    normalizedItems.forEach((_, idx) => {
      next[idx] = true;
    });
    setOpenMap(next);
  }, [normalizedItems, defaultExpanded]);

  const toggleAt = (idx, next) => {
    setOpenMap((prev) => ({
      ...prev,
      [idx]: typeof next === "boolean" ? next : !prev[idx],
    }));
  };

  return (
    <MKBox sx={{ width: "100%", px: 0 }}>
      <Grid container rowSpacing={2.5} columnSpacing={2.5} sx={{ width: "100%", m: 0 }}>
        {normalizedItems.map((it, idx) => (
          <Grid item xs={12} md={6} key={it.key || `faq-${idx}`} sx={{ p: 0 }}>
            {/* 1 col on xs, 2 cols on md+ */}
            <Question
              question={it.q}
              answer={it.a}
              expanded={!!openMap[idx]}
              onToggle={(next) => toggleAt(idx, next)}
            />
          </Grid>
        ))}
      </Grid>
    </MKBox>
  );
}

FAQ.propTypes = {
  // Q&A list: [{ q: string, a: string, key?: string }]
  items: PropTypes.arrayOf(
    PropTypes.shape({
      // supported shapes: {q,a} or {question,answer}
      q: PropTypes.string,
      a: PropTypes.string,
      question: PropTypes.string,
      answer: PropTypes.string,
      key: PropTypes.string,
      pk: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ),
  // if true, expand all items by default
  defaultExpanded: PropTypes.bool,
};

FAQ.defaultProps = {
  items: [],
  defaultExpanded: false,
};

export default FAQ;
